import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, UserCircle2 } from "lucide-react";
import { openWhatsApp, setWaNumber } from "@/lib/whatsapp";
import api from "@/lib/api";
import WhatsAppButton from "@/components/public/WhatsAppButton";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/a-propos", label: "À propos" },
  { to: "/solutions", label: "Solutions" },
  { to: "/services", label: "Services" },
  { to: "/produits", label: "Hardware" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState({});
  const location = useLocation();

  useEffect(() => {
    api.get("/settings").then(({ data }) => { setSettings(data || {}); setWaNumber(data?.whatsapp); }).catch(() => {});
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => { api.post("/analytics/track", { path: location.pathname }).catch(() => {}); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-slate-900">
      <header className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[74px]">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2">
            <img src="/logo.webp" alt="VISION LOGICIELS" className="h-9 sm:h-10 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) => `px-4 py-2 text-sm font-medium rounded-full transition-colors ${isActive ? "text-[#0F2A6B] bg-[#0F2A6B]/5" : "text-slate-600 hover:text-[#0F2A6B]"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/espace-client" data-testid="nav-portal-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0F2A6B] transition-colors"><UserCircle2 className="w-4 h-4" /> Espace client</Link>
            <button data-testid="nav-advisor-button" onClick={() => openWhatsApp({ page: "header", message: "Bonjour, je souhaite parler à un conseiller VISION LOGICIELS." })}
              className="text-sm font-semibold text-[#0F2A6B] hover:text-[#4CB944] transition-colors">Parler à un conseiller</button>
            <Link to="/devis" data-testid="nav-devis-cta-button"
              className="px-5 py-2.5 rounded-full bg-[#0F2A6B] text-white text-sm font-semibold hover:bg-[#0A1F52] transition-all hover:shadow-lg hover:shadow-[#0F2A6B]/25">
              Demander un devis
            </Link>
          </div>
          <button data-testid="nav-mobile-toggle" className="lg:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-200 px-4 py-4 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `block px-4 py-3 rounded-xl font-medium ${isActive ? "bg-[#0F2A6B]/5 text-[#0F2A6B]" : "text-slate-700"}`}>{n.label}</NavLink>
            ))}
            <Link to="/espace-client" data-testid="nav-mobile-portal-link" className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-slate-700"><UserCircle2 className="w-4 h-4" /> Espace client</Link>
            <Link to="/devis" className="block text-center mt-2 px-5 py-3 rounded-xl bg-[#0F2A6B] text-white font-semibold">Demander un devis</Link>
          </div>
        )}
      </header>

      <main className="pt-[74px]"><Outlet /></main>

      <footer className="bg-[#0A1F52] text-slate-300 relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid gap-10 md:grid-cols-4 relative">
          <div className="md:col-span-1">
            <img src="/logo.webp" alt="VISION LOGICIELS" className="h-10 w-auto bg-white rounded-lg p-1.5 mb-4" />
            <p className="text-sm leading-relaxed text-slate-400">{settings.signature || "Transformer vos besoins en solutions technologiques performantes."}</p>
            <p className="text-xs text-slate-500 mt-4">Une marque commerciale de <span className="text-[#4CB944] font-semibold">PERFORM & CO</span></p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-head">Navigation</h4>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-[#4CB944] transition-colors">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-head">Expertises</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>Développement logiciel</li><li>Infrastructure & Réseaux</li>
              <li>Cybersécurité</li><li>Maintenance IT</li><li>Fourniture de matériel</li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-head">Contact</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#4CB944]" />{settings.address || "Abidjan – Cocody Riviera 3"}</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#4CB944]" />{settings.phone || "+225 07 98 64 77 83"}</li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#4CB944]" />{settings.email || "info@visionlogiciels.com"}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500 relative">
          © {new Date().getFullYear()} VISION LOGICIELS — PERFORM & CO. Tous droits réservés. · <Link to="/admin/login" className="hover:text-[#4CB944]">Back Office</Link>
        </div>
      </footer>

      <WhatsAppButton page={location.pathname} />
    </div>
  );
}
